import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { getCountries, getProvinces, getCategories, updateUserProfile } from '../../services/api';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Instagram, MapPin, CheckCircle2, ShieldCheck, ArrowRight, ArrowLeft, Loader2, ChevronDown, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { beginInstagramConnect } from '../../utils/instagramConnect';
import './OnboardingWizard.scss';

const CreatorOnboardingWizard = ({ initialData, returnTo = '' }) => {
  const { t } = useTranslation('creator');
  const navigate = useNavigate();
  const { currentUser, refreshUser } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [profileSaving, setProfileSaving] = useState(false);
  const [instagramOpening, setInstagramOpening] = useState(false);
  const [countries, setCountries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [categoriesLoadFailed, setCategoriesLoadFailed] = useState(false);
  const [error, setError] = useState('');

  const [selectedCategories, setSelectedCategories] = useState(initialData?.content_categories || []);
  const [selectedCountry, setSelectedCountry] = useState(initialData?.country ? initialData.country.toString() : '');
  const [city, setCity] = useState(initialData?.city || '');
  const [searchCountry, setSearchCountry] = useState('');
  const [phone, setPhone] = useState(initialData?.phone_number || '');

  const [provinces, setProvinces] = useState([]);
  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [searchProvince, setSearchProvince] = useState('');
  const [showProvinceDropdown, setShowProvinceDropdown] = useState(false);
  const instagramResetRef = useRef(null);

  const hasInstagram = currentUser?.creator_profile?.instagram_account_connected;
  const isInvited = Boolean(
    currentUser?.creator_profile?.is_invited
    || currentUser?.creator_profile?.invited_collab
    || currentUser?.creator_profile?.invited_offer_ids?.length > 0
  );
  const completionTarget = returnTo || (
    isInvited
      ? '/creator/invited'
      : '/creator/pending-approval'
  );

  useEffect(() => {
    const fetchDropdownData = async () => {
      setLoadingCategories(true);
      setCategoriesLoadFailed(false);
      try {
        const [countriesRes, categoriesRes] = await Promise.all([
          getCountries(),
          getCategories('creator')
        ]);
        setCountries(countriesRes.data);
        setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
      } catch (err) {
        console.error('Error loading dropdown data:', err);
        setCategoriesLoadFailed(true);
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchDropdownData();
  }, []);

  useEffect(() => {
    if (!selectedCountry) {
      setProvinces([]);
      setCity('');
      return;
    }

    const fetchProvinces = async () => {
      setLoadingProvinces(true);
      try {
        const response = await getProvinces(selectedCountry);
        setProvinces(response.data);
        setCity('');

        const countryObj = countries.find(c => c.id.toString() === selectedCountry);
        if (countryObj?.name === 'UNITED STATES') {
          setCity('Miami');
        }

        setSearchProvince('');
      } catch (err) {
        console.error('Error fetching provinces:', err);
      } finally {
        setLoadingProvinces(false);
      }
    };

    fetchProvinces();
  }, [selectedCountry, countries]);

  const handleCategoryToggle = (categoryId) => {
    const idStr = categoryId.toString();
    setSelectedCategories(prev =>
      prev.includes(idStr) ? prev.filter(id => id !== idStr) : [...prev, idStr]
    );
  };

  const handleInstagramConnect = () => {
    if (instagramOpening) return;
    setInstagramOpening(true);
    try {
      beginInstagramConnect(completionTarget);
    } catch (err) {
      console.error('Error opening Instagram authorization:', err);
      setInstagramOpening(false);
      return;
    }
    window.clearTimeout(instagramResetRef.current);
    instagramResetRef.current = window.setTimeout(() => setInstagramOpening(false), 4000);
  };

  const handleSubmitProfile = async () => {
    if (!selectedCountry || !city || !phone) {
      setError(t('onboarding.errors.required_fields'));
      return;
    }

    setProfileSaving(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('country', selectedCountry);
      formData.append('city', city);
      formData.append('phone_number', phone);
      formData.append('first_name', currentUser?.first_name || 'Creator');
      formData.append('last_name', currentUser?.last_name || 'FOTH');
      selectedCategories.forEach(cat => formData.append('content_categories', cat));

      await updateUserProfile(formData);
      const refreshedUser = await refreshUser();
      const instagramConnected = refreshedUser?.creator_profile?.instagram_account_connected || hasInstagram;

      if (instagramConnected) {
        navigate(completionTarget);
      } else {
        setCurrentStep(3);
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(t('onboarding.errors.save_error'));
    } finally {
      setProfileSaving(false);
    }
  };

  useEffect(() => () => window.clearTimeout(instagramResetRef.current), []);

  const usCountry = countries.find(c => c.name === 'UNITED STATES');
  const latinAmericaCountries = countries.filter(c => c.name !== 'UNITED STATES');
  const filteredLatinCountries = latinAmericaCountries.filter(c =>
    c.name.toLowerCase().includes(searchCountry.toLowerCase())
  );

  return (
    <div className="wizard-container">
      <div className="wizard-progress">
        <div className="progress-bar" style={{ width: `${(currentStep / 3) * 100}%` }} />
      </div>

      <AnimatePresence mode="wait">
        {currentStep === 1 && (
          <motion.div
            key="step-cats"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="wizard-step"
          >
            <div className="step-header">
              <h2>{t('onboarding.step_cats.title')}</h2>
              <p>{t('onboarding.step_cats.subtitle')}</p>
            </div>

            <div className="categories-selection">
              {loadingCategories ? (
                <div className="wizard-inline-state">
                  <Loader2 className="spin" size={16} />
                  <span>{t('onboarding.step_cats.loading', 'Loading categories...')}</span>
                </div>
              ) : categories.length > 0 ? (
                <div className="categories-pills">
                  {categories.map(cat => (
                    <button
                      key={cat.id}
                      type="button"
                      className={`pill ${selectedCategories.includes(cat.id.toString()) ? 'active' : ''}`}
                      onClick={() => handleCategoryToggle(cat.id)}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="wizard-inline-state">
                  <span>
                    {categoriesLoadFailed
                      ? t('onboarding.step_cats.load_error', 'We could not load categories right now. You can continue and update them later.')
                      : t('onboarding.step_cats.empty', 'Categories are not available right now. You can continue and update them later.')}
                  </span>
                </div>
              )}
            </div>

            <div className="wizard-actions">
              <button
                className="btn-next"
                disabled={loadingCategories || (categories.length > 0 && selectedCategories.length === 0)}
                onClick={() => setCurrentStep(2)}
              >
                {t('onboarding.buttons.continue')} <ArrowRight size={18} />
              </button>
            </div>
          </motion.div>
        )}

        {currentStep === 2 && (
          <motion.div
            key="step-location"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="wizard-step"
          >
            <div className="step-header">
              <h2>{t('onboarding.step2.title')}</h2>
              <p>{t('onboarding.step2.subtitle')}</p>
            </div>

            <div className="location-inputs">
              <div className="form-group mb-4">
                <label>{t('onboarding.step2.country_label')}</label>
                <div className="input-with-icon">
                  <MapPin size={18} />
                  <input
                    type="text"
                    className="wizard-input search-input"
                    placeholder={t('onboarding.step2.country_placeholder')}
                    value={searchCountry}
                    onChange={(e) => setSearchCountry(e.target.value)}
                  />
                </div>

                <div className="country-grid-wrapper">
                  {usCountry && (
                    <div className="special-country-section mb-4">
                      <button
                        type="button"
                        className={`country-card ${selectedCountry === usCountry.id.toString() ? 'selected' : ''}`}
                        onClick={() => setSelectedCountry(usCountry.id.toString())}
                      >
                        <div className="country-name">{usCountry.name}</div>
                        {selectedCountry === usCountry.id.toString() && <CheckCircle2 size={16} className="text-gold" />}
                      </button>
                      <div className="section-divider mt-4 mb-2">
                        <span>Rest of America</span>
                      </div>
                    </div>
                  )}

                  <div className="country-grid">
                    {filteredLatinCountries.length > 0 ? filteredLatinCountries.map(country => (
                      <button
                        type="button"
                        key={country.id}
                        className={`country-card ${selectedCountry === country.id.toString() ? 'selected' : ''}`}
                        onClick={() => setSelectedCountry(country.id.toString())}
                      >
                        <div className="country-name">{country.name}</div>
                        {selectedCountry === country.id.toString() && <CheckCircle2 size={16} className="text-gold" />}
                      </button>
                    )) : (
                      <p className="no-results">{t('onboarding.step2.no_countries')}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-group mb-4">
                <label>
                  {selectedCountry
                    ? (countries.find(c => c.id.toString() === selectedCountry)?.division_name || t('onboarding.step2.city_label'))
                    : t('onboarding.step2.city_label')
                  }
                </label>

                <div className="custom-select-container">
                  <button
                    type="button"
                    className={`custom-select-trigger ${!selectedCountry ? 'disabled' : ''}`}
                    onClick={() => selectedCountry && setShowProvinceDropdown(!showProvinceDropdown)}
                  >
                    <MapPin size={18} />
                    <span>
                      {city || (selectedCountry ? `Selecciona ${countries.find(c => c.id.toString() === selectedCountry)?.division_name || '...'}` : t('onboarding.step2.city_placeholder'))}
                    </span>
                    <ChevronDown size={18} className={`chevron ${showProvinceDropdown ? 'open' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {showProvinceDropdown && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="custom-select-dropdown"
                      >
                        <div className="dropdown-search">
                          <Search size={14} />
                          <input
                            type="text"
                            placeholder="Buscar..."
                            value={searchProvince}
                            onChange={(e) => setSearchProvince(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="dropdown-options">
                          {provinces
                            .filter(p => p.name.toLowerCase().includes(searchProvince.toLowerCase()))
                            .map(province => (
                              <button
                                type="button"
                                key={province.id}
                                className={`dropdown-option ${city === province.name ? 'selected' : ''}`}
                                onClick={() => {
                                  setCity(province.name);
                                  setShowProvinceDropdown(false);
                                  setSearchProvince('');
                                }}
                              >
                                {province.name}
                              </button>
                            ))
                          }
                          {loadingProvinces && <div className="dropdown-loading"><Loader2 className="spin" size={16} /></div>}
                          {!loadingProvinces && provinces.length === 0 && <div className="dropdown-no-results">No hay resultados</div>}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <div className="form-group">
                <label>{t('onboarding.step3.phone_label')}</label>
                <PhoneInput
                  country={selectedCountry ? countries.find(c => c.id.toString() === selectedCountry)?.code.toLowerCase() : 'pa'}
                  value={phone}
                  onChange={setPhone}
                  containerClass="phone-input-container wizard-phone"
                  inputClass="wizard-input"
                />
              </div>
            </div>

            {error && <div className="wizard-error">{error}</div>}

            <div className="wizard-actions dual">
              <button className="btn-back" onClick={() => setCurrentStep(1)}>
                <ArrowLeft size={16} /> {t('onboarding.buttons.back')}
              </button>
              <button
                className="btn-submit"
                disabled={!selectedCountry || !city || !phone || profileSaving}
                onClick={handleSubmitProfile}
              >
                {profileSaving ? <Loader2 className="spin" size={20} /> : t('onboarding.buttons.continue')}
              </button>
            </div>
          </motion.div>
        )}

        {currentStep === 3 && (
          <motion.div
            key="step-instagram"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="wizard-step"
          >
            <div className="step-header">
              <h2>{t('onboarding.step1.title')}</h2>
              <p>{t('onboarding.step1.subtitle')}</p>
            </div>

            <div className="instagram-connect-box">
              <div className="ig-icon-wrapper">
                <Instagram size={40} />
              </div>
              <h3>{t('onboarding.step1.connect_title')}</h3>
              <p className="ig-subtitle">{t('onboarding.step1.connect_subtitle')}</p>

              <button
                className="btn-instagram-onboarding"
                onClick={handleInstagramConnect}
                disabled={instagramOpening}
              >
                {instagramOpening ? <Loader2 className="spin" size={20} /> : <Instagram size={20} />}
                {instagramOpening ? t('onboarding.step1.connecting') : t('onboarding.step1.connect_button')}
              </button>
            </div>

            <div className="security-disclaimer">
              <ShieldCheck size={20} className="text-gold" />
              <div className="disclaimer-text">
                <strong>{t('onboarding.step1.disclaimer').split('.')[0]}.</strong> {t('onboarding.step1.disclaimer').split('.').slice(1).join('.')}
              </div>
            </div>

            <div className="wizard-actions">
              <button className="btn-back" onClick={() => setCurrentStep(2)}>
                <ArrowLeft size={16} /> {t('onboarding.buttons.back')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CreatorOnboardingWizard;

